/**
 * Sprint 16 — Start Live Operations Wizard.
 *
 * Owner-only 7-step wizard that establishes the tenant's Day Zero
 * (default 2026-08-01) and sets an operational baseline:
 *   1. Start Date   2. Opening Balances   3. Opening Inventory
 *   4. Open Payables 5. Staff Confirm  6. Recurring Payments 7. Confirm
 *
 * Consumes:
 *   GET  /api/day-zero/status
 *   GET  /api/day-zero/defaults
 *   POST /api/day-zero/start   (owner)
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { money } from "@/lib/format";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  CalendarClock, Banknote, Boxes, FileClock, Users, Repeat, CheckCircle2,
  Rocket, ChevronLeft, ChevronRight, Plus, Trash2, AlertTriangle,
} from "lucide-react";

const L = {
  tr: {
    title: "Canlı Operasyonu Başlat",
    subtitle: "Bugünden itibaren operasyonel açılış konfigürasyonu",
    stepOf: (i, n) => `Adım ${i} / ${n}`,
    next: "İleri", back: "Geri", cancel: "İptal", finish: "Canlı Operasyonu Başlat",
    steps: [
      "Başlangıç Tarihi", "Açılış Bakiyeleri", "Açılış Envanteri",
      "Açık Borçlar", "Personel", "Sabit Aylık Ödemeler", "Onayla",
    ],
    startDate: "Canlı başlangıç tarihi",
    startDateHelp: "01.08.2026 sonrasında her fatura, satış ve stok hareketi kalıcı canlı geçmiş olur.",
    liveStartExplain: "01.08.2026'dan önce oluşturulan tüm veriler arşive taşınacak.",
    openingBalances: "Açılış Bakiyeleri",
    bankBalance: "Banka Bakiyesi",
    cashBalance: "Kasa Bakiyesi",
    notes: "Not",
    openingInventory: "Açılış Envanteri",
    openingInventoryHelp: "Canlı başlangıçta stoğunda bulunan malzemeler.",
    addItem: "Kalem ekle",
    itemName: "Ad", unit: "Birim", quantity: "Miktar",
    minStock: "Min. Stok", costPerUnit: "Birim Maliyet", supplier: "Tedarikçi",
    openPayables: "Açık Borçlar (Ödenmemiş Faturalar)",
    openPayablesHelp: "Canlı başlangıçta ödenmemiş kalan tedarikçi faturaları.",
    addPayable: "Borç ekle",
    payableSupplier: "Tedarikçi", payableAmount: "Tutar",
    payableDueDate: "Vade", payableCategory: "Kategori", payableRecurring: "Tekrarlayan",
    staff: "Aktif Personel",
    staffHelp: "Yalnızca onay; personel modülünü yeniden düzenlemiyoruz.",
    confirmStaff: "Onayla",
    recurring: "Sabit Aylık Ödemeler",
    recurringHelp: "Beklenen tekrarlayan giderler.",
    label: "Etiket", amount: "Tutar", dayOfMonth: "Ay içi gün",
    confirmTitle: "Canlı Operasyonu Başlatmak Üzeresin",
    confirmBody: "Onaylandığında:",
    li1: "Tenant.live_start_date =",
    li2: "01.08.2026 öncesi tüm işlem verisi arşive taşınır (silinmez).",
    li3: "Açılış bakiyeleri, envanter, borçlar ve tekrarlayan ödemeler oluşturulur.",
    li4: "Canlı Dashboard, Asistan ve Bugün Motoru arşive dokunmaz.",
    successToast: "Canlı operasyon hazır!",
    launchIn: (n) => `${n} gün sonra canlı`,
    liveNow: "Canlı!",
    livePending: "Canlı beklemede",
    activeStaff: "aktif personel",
    ownerOnly: "Bu sihirbaz yalnızca işletme sahibine açıktır.",
    // Sprint 18
    pastDateError: "Geçmiş tarih seçilemez — bugünü veya sonrasını seç.",
    farFutureError: "İki yıldan uzak tarih seçilemez.",
    guardrailsTitle: "Devam etmeden önce",
    guardrailZeroBalances: "Açılış bakiyeleri sıfır — bu bilinçli mi?",
    guardrailNoInventory: "Açılış envanteri boş — canlıda stok takibi yapılamaz.",
    guardrailNoRecurring: "Sabit ödeme eklenmedi — kira, ÖGK vb. için tavsiye edilir.",
    confirmLaunchTitle: "Canlı operasyonu başlat?",
    confirmLaunchBody: (d) =>
      `${d} tarihi Day Zero olacak. Öncesindeki tüm veri arşive taşınacak. 72 saat içinde geri alabilirsin.`,
    confirmLaunchYes: "Evet, başlat",
    confirmLaunchNo: "Vazgeç",
    launchedTitle: "Canlı Operasyon Hazır",
    launchedBody: (d) => `${d} itibarıyla GastroPilot canlı çalışacak.`,
    goToDashboard: "Panele dön",
    canUndo: "72 saat içinde geri alınabilir",
  },
  de: {
    title: "Live-Betrieb starten",
    subtitle: "Operativer Startaufbau ab Tag Null",
    stepOf: (i, n) => `Schritt ${i} / ${n}`,
    next: "Weiter", back: "Zurück", cancel: "Abbrechen", finish: "Live-Betrieb starten",
    steps: [
      "Startdatum", "Eröffnungsbilanzen", "Anfangsbestand",
      "Offene Verbindlichkeiten", "Personal", "Fixe Monatszahlungen", "Bestätigen",
    ],
    startDate: "Live-Startdatum",
    startDateHelp: "Ab dem 01.08.2026 werden alle Rechnungen, Verkäufe und Bestandsbewegungen zur permanenten Live-Historie.",
    liveStartExplain: "Alle Daten vor dem 01.08.2026 werden ins Archiv verschoben.",
    openingBalances: "Eröffnungsbilanzen",
    bankBalance: "Bankguthaben",
    cashBalance: "Kassenbestand",
    notes: "Notiz",
    openingInventory: "Anfangsbestand",
    openingInventoryHelp: "Artikel, die am Live-Start im Bestand sind.",
    addItem: "Artikel hinzufügen",
    itemName: "Name", unit: "Einheit", quantity: "Menge",
    minStock: "Min. Bestand", costPerUnit: "Kosten pro Einheit", supplier: "Lieferant",
    openPayables: "Offene Verbindlichkeiten",
    openPayablesHelp: "Unbezahlte Lieferantenrechnungen am Live-Start.",
    addPayable: "Verbindlichkeit hinzufügen",
    payableSupplier: "Lieferant", payableAmount: "Betrag",
    payableDueDate: "Fällig am", payableCategory: "Kategorie", payableRecurring: "Wiederkehrend",
    staff: "Aktives Personal",
    staffHelp: "Nur Bestätigung — das Personalmodul wird nicht neu gestaltet.",
    confirmStaff: "Bestätigen",
    recurring: "Fixe Monatszahlungen",
    recurringHelp: "Erwartete wiederkehrende Ausgaben.",
    label: "Bezeichnung", amount: "Betrag", dayOfMonth: "Tag im Monat",
    confirmTitle: "Live-Betrieb wird gleich gestartet",
    confirmBody: "Bei Bestätigung passiert Folgendes:",
    li1: "Tenant.live_start_date =",
    li2: "Alle Transaktionen vor dem 01.08.2026 werden archiviert (nicht gelöscht).",
    li3: "Eröffnungsbilanzen, Bestand, Verbindlichkeiten & wiederkehrende Zahlungen werden angelegt.",
    li4: "Live-Dashboard, Assistant und Today-Engine ignorieren Archivdaten.",
    successToast: "Live-Betrieb bereit!",
    launchIn: (n) => `Live in ${n} Tagen`,
    liveNow: "Live!",
    livePending: "Live geplant",
    activeStaff: "aktives Personal",
    ownerOnly: "Dieser Assistent ist nur für den Inhaber verfügbar.",
    // Sprint 18
    pastDateError: "Vergangenes Datum nicht erlaubt — heute oder später wählen.",
    farFutureError: "Datum darf höchstens 2 Jahre in der Zukunft liegen.",
    guardrailsTitle: "Vor dem Start",
    guardrailZeroBalances: "Eröffnungssalden sind 0 — ist das gewollt?",
    guardrailNoInventory: "Anfangsbestand ist leer — kein Live-Lagerabgleich möglich.",
    guardrailNoRecurring: "Keine Fixzahlungen — Miete, ÖGK etc. empfohlen.",
    confirmLaunchTitle: "Live-Betrieb starten?",
    confirmLaunchBody: (d) =>
      `${d} wird der Day Zero. Alle vorherigen Daten werden ins Archiv verschoben. Rückgängig innerhalb von 72 Stunden möglich.`,
    confirmLaunchYes: "Ja, starten",
    confirmLaunchNo: "Abbrechen",
    launchedTitle: "Live-Betrieb bereit",
    launchedBody: (d) => `Ab ${d} läuft GastroPilot live.`,
    goToDashboard: "Zum Dashboard",
    canUndo: "Rückgängig innerhalb von 72 Stunden möglich",
  },
};

const STEP_ICONS = [CalendarClock, Banknote, Boxes, FileClock, Users, Repeat, CheckCircle2];

// ---------- Small helpers ----------
function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso + "T00:00:00Z");
  const now = new Date();
  return Math.ceil((d - now) / (1000 * 60 * 60 * 24));
}

// ============================================================
//  Main wizard modal
// ============================================================
export default function StartLiveOpsWizard({ open, onOpenChange, onCompleted, initialStep = 0 }) {
  const { lang } = useI18n();
  const { user } = useAuth();
  const t = L[lang] || L.tr;

  const [step, setStep] = useState(initialStep);
  const [defaults, setDefaults] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Form state (all seven steps)
  const [liveStart, setLiveStart] = useState("2026-08-01");
  const [bankBalance, setBankBalance] = useState("");
  const [cashBalance, setCashBalance] = useState("");
  const [notes, setNotes] = useState("");
  const [inventory, setInventory] = useState([]);
  const [payables, setPayables] = useState([]);
  const [confirmedStaff, setConfirmedStaff] = useState({});
  const [recurring, setRecurring] = useState([]);
  // Sprint 18 — launch safety
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [launched, setLaunched] = useState(null);   // holds success response

  // today's ISO date for min-attribute + validation.
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const dateInvalid = useMemo(() => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(liveStart)) return null;
    if (liveStart < todayIso) return t.pastDateError;
    const dt = new Date(liveStart + "T00:00:00Z");
    const in2y = new Date();
    in2y.setUTCFullYear(in2y.getUTCFullYear() + 2);
    if (dt > in2y) return t.farFutureError;
    return null;
  }, [liveStart, todayIso, t]);

  // guardrail warnings (soft, displayed on step 7).
  const guardrails = useMemo(() => {
    const w = [];
    const bank = parseFloat(bankBalance || "0") || 0;
    const cash = parseFloat(cashBalance || "0") || 0;
    if (bank === 0 && cash === 0) w.push(t.guardrailZeroBalances);
    if (!inventory.filter((it) => it.name && it.name.trim()).length) w.push(t.guardrailNoInventory);
    if (!recurring.filter((r) => r.label && parseFloat(r.amount || "0") > 0).length) w.push(t.guardrailNoRecurring);
    return w;
  }, [bankBalance, cashBalance, inventory, recurring, t]);

  useEffect(() => {
    if (!open) return;
    setStep(initialStep || 0);
    setLaunched(null);
    (async () => {
      try {
        const { data } = await api.get("/day-zero/defaults");
        setDefaults(data);
        setLiveStart(data.default_live_start_date);
        setRecurring(data.recurring_payment_seeds.map((s) => ({ ...s })));
        // Pre-confirm all active staff by default.
        const map = {};
        data.active_staff.forEach((s) => (map[s.id] = true));
        setConfirmedStaff(map);
      } catch (e) {
        toast.error("Failed to load defaults");
      }
    })();
  }, [open]);

  const isOwner = user?.role === "owner";
  const totalSteps = t.steps.length;

  const canNext = useMemo(() => {
    if (step === 0) return /^\d{4}-\d{2}-\d{2}$/.test(liveStart) && !dateInvalid;
    return true;
  }, [step, liveStart, dateInvalid]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        live_start_date: liveStart,
        opening_balances: {
          bank_balance: parseFloat(bankBalance || "0") || 0,
          cash_balance: parseFloat(cashBalance || "0") || 0,
          notes,
        },
        opening_inventory: inventory
          .filter((it) => it.name && it.name.trim())
          .map((it) => ({
            name: it.name.trim(),
            unit: it.unit || "pcs",
            quantity: parseFloat(it.quantity || "0") || 0,
            min_stock: parseFloat(it.min_stock || "0") || 0,
            cost: parseFloat(it.cost || "0") || 0,
            supplier: it.supplier || "",
          })),
        open_payables: payables
          .filter((p) => p.supplier && parseFloat(p.amount || "0") > 0)
          .map((p) => ({
            supplier: p.supplier.trim(),
            amount: parseFloat(p.amount) || 0,
            due_date: p.due_date || liveStart,
            category_id: p.category_id || null,
            recurring: !!p.recurring,
          })),
        recurring_payments: recurring
          .filter((r) => r.label && parseFloat(r.amount || "0") > 0)
          .map((r) => ({
            label: r.label,
            vendor: r.vendor || r.label,
            category: r.category || "other",
            amount: parseFloat(r.amount) || 0,
            day_of_month: parseInt(r.day_of_month, 10) || 1,
          })),
        confirmed_staff_ids: Object.entries(confirmedStaff)
          .filter(([, v]) => v)
          .map(([k]) => k),
      };
      const { data } = await api.post("/day-zero/start", payload);
      toast.success(t.successToast);
      setLaunched(data);                // show success view inside the dialog
      onCompleted?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start");
    } finally {
      setSubmitting(false);
      setConfirmOpen(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="day-zero-wizard"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Rocket className="w-5 h-5 text-primary" /> {t.title}
          </DialogTitle>
          <DialogDescription>{t.subtitle}</DialogDescription>
        </DialogHeader>

        {launched ? (
          <LaunchedView t={t} result={launched} onClose={() => { setLaunched(null); onOpenChange(false); }} />
        ) : !isOwner ? (
          <div className="py-8 flex items-center gap-3 text-sm text-amber-800">
            <AlertTriangle className="w-4 h-4" /> {t.ownerOnly}
          </div>
        ) : (
          <>
            <Stepper step={step} labels={t.steps} />

            <div className="min-h-[280px] py-4">
              {step === 0 && (
                <StepStartDate
                  t={t} liveStart={liveStart} setLiveStart={setLiveStart}
                  todayIso={todayIso} error={dateInvalid}
                />
              )}
              {step === 1 && (
                <StepOpeningBalances
                  t={t}
                  bankBalance={bankBalance} setBankBalance={setBankBalance}
                  cashBalance={cashBalance} setCashBalance={setCashBalance}
                  notes={notes} setNotes={setNotes}
                />
              )}
              {step === 2 && (
                <StepInventory t={t} rows={inventory} setRows={setInventory} />
              )}
              {step === 3 && (
                <StepPayables t={t} rows={payables} setRows={setPayables} />
              )}
              {step === 4 && (
                <StepStaff
                  t={t} staff={defaults?.active_staff || []}
                  confirmed={confirmedStaff} setConfirmed={setConfirmedStaff}
                />
              )}
              {step === 5 && (
                <StepRecurring t={t} rows={recurring} setRows={setRecurring} />
              )}
              {step === 6 && (
                <StepConfirm
                  t={t}
                  liveStart={liveStart}
                  bankBalance={bankBalance}
                  cashBalance={cashBalance}
                  inventoryCount={inventory.filter((i) => i.name && i.name.trim()).length}
                  payablesCount={payables.filter((p) => p.supplier && parseFloat(p.amount || "0") > 0).length}
                  recurringCount={recurring.filter((r) => r.label && parseFloat(r.amount || "0") > 0).length}
                  staffCount={Object.values(confirmedStaff).filter(Boolean).length}
                  guardrails={guardrails}
                />
              )}
            </div>

            <DialogFooter className="flex items-center justify-between gap-2 sm:justify-between">
              <div className="flex items-center gap-2">
                <Badge variant="outline" data-testid="wizard-step-indicator">
                  {t.stepOf(step + 1, totalSteps)}
                </Badge>
                {step === 0 && liveStart && !dateInvalid && (
                  <Badge variant="secondary" data-testid="wizard-launch-hint">
                    {daysUntil(liveStart) > 0 ? t.launchIn(daysUntil(liveStart)) : t.liveNow}
                  </Badge>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => (step === 0 ? onOpenChange(false) : setStep(step - 1))}
                  data-testid="wizard-back"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  {step === 0 ? t.cancel : t.back}
                </Button>
                {step < totalSteps - 1 ? (
                  <Button
                    onClick={() => setStep(step + 1)}
                    disabled={!canNext}
                    data-testid="wizard-next"
                  >
                    {t.next} <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                ) : (
                  <Button
                    onClick={() => setConfirmOpen(true)}
                    disabled={submitting}
                    data-testid="wizard-finish"
                    className="bg-primary"
                  >
                    <Rocket className="w-4 h-4 mr-2" />
                    {submitting ? "…" : t.finish}
                  </Button>
                )}
              </div>
            </DialogFooter>

            {/* Sprint 18 — Launch confirmation AlertDialog */}
            <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
              <AlertDialogContent data-testid="launch-confirm-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2">
                    <Rocket className="w-5 h-5 text-primary" /> {t.confirmLaunchTitle}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {t.confirmLaunchBody(liveStart)}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="launch-confirm-cancel">
                    {t.confirmLaunchNo}
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={submit}
                    disabled={submitting}
                    data-testid="launch-confirm-yes"
                  >
                    <Rocket className="w-4 h-4 mr-2" /> {t.confirmLaunchYes}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
//  Stepper
// ============================================================
function Stepper({ step, labels }) {
  return (
    <div className="flex items-center gap-1 overflow-x-auto pb-2">
      {labels.map((label, i) => {
        const Icon = STEP_ICONS[i] || CheckCircle2;
        const active = i === step;
        const done = i < step;
        return (
          <div key={label} className="flex items-center gap-1 shrink-0">
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] border ${
                done
                  ? "bg-primary text-primary-foreground border-primary"
                  : active
                    ? "border-primary text-primary bg-primary/10"
                    : "border-muted-foreground/30 text-muted-foreground"
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
            </div>
            <div className={`text-[10px] uppercase tracking-wide hidden md:block ${
              active ? "text-primary font-semibold" : "text-muted-foreground"
            }`}>{label}</div>
            {i < labels.length - 1 && <div className="w-4 h-px bg-muted-foreground/30 mx-1" />}
          </div>
        );
      })}
    </div>
  );
}

// ============================================================
//  Step 1 — Start Date
// ============================================================
function StepStartDate({ t, liveStart, setLiveStart, todayIso, error }) {
  return (
    <div className="space-y-4">
      <div>
        <Label>{t.startDate}</Label>
        <Input
          type="date"
          value={liveStart}
          min={todayIso}
          onChange={(e) => setLiveStart(e.target.value)}
          data-testid="wizard-live-start-date"
          className={`max-w-xs mt-2 ${error ? "border-destructive focus-visible:ring-destructive" : ""}`}
        />
        {error && (
          <div className="mt-2 text-xs text-destructive flex items-center gap-1"
               data-testid="wizard-live-start-error">
            <AlertTriangle className="w-3 h-3" /> {error}
          </div>
        )}
      </div>
      <p className="text-sm text-muted-foreground">{t.startDateHelp}</p>
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
        <AlertTriangle className="w-4 h-4 inline mr-1" />
        {t.liveStartExplain}
      </div>
    </div>
  );
}

// ============================================================
//  Step 2 — Opening Balances
// ============================================================
function StepOpeningBalances({ t, bankBalance, setBankBalance, cashBalance, setCashBalance, notes, setNotes }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label>{t.bankBalance}</Label>
          <Input
            type="number" inputMode="decimal" step="0.01"
            value={bankBalance} onChange={(e) => setBankBalance(e.target.value)}
            placeholder="0.00"
            data-testid="wizard-bank-balance"
          />
        </div>
        <div>
          <Label>{t.cashBalance}</Label>
          <Input
            type="number" inputMode="decimal" step="0.01"
            value={cashBalance} onChange={(e) => setCashBalance(e.target.value)}
            placeholder="0.00"
            data-testid="wizard-cash-balance"
          />
        </div>
      </div>
      <div>
        <Label>{t.notes}</Label>
        <Input
          value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder=""
          data-testid="wizard-notes"
        />
      </div>
    </div>
  );
}

// ============================================================
//  Step 3 — Inventory rows
// ============================================================
function StepInventory({ t, rows, setRows }) {
  const add = () => setRows([...rows, { name: "", unit: "kg", quantity: "", min_stock: "", cost: "", supplier: "" }]);
  const upd = (i, k, v) => { const c = [...rows]; c[i] = { ...c[i], [k]: v }; setRows(c); };
  const rm = (i) => setRows(rows.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">{t.openingInventoryHelp}</p>
      <div className="space-y-2 max-h-[260px] overflow-y-auto pr-1">
        {rows.map((r, i) => (
          <Card key={i} className="border">
            <CardContent className="p-3 grid grid-cols-2 sm:grid-cols-6 gap-2 items-end">
              <div className="col-span-2 sm:col-span-2">
                <Label className="text-[10px]">{t.itemName}</Label>
                <Input value={r.name} onChange={(e) => upd(i, "name", e.target.value)}
                       data-testid={`wizard-inv-name-${i}`} placeholder="e.g. Rice" />
              </div>
              <div>
                <Label className="text-[10px]">{t.unit}</Label>
                <Input value={r.unit} onChange={(e) => upd(i, "unit", e.target.value)} placeholder="kg" />
              </div>
              <div>
                <Label className="text-[10px]">{t.quantity}</Label>
                <Input type="number" step="0.01" value={r.quantity}
                       onChange={(e) => upd(i, "quantity", e.target.value)}
                       data-testid={`wizard-inv-qty-${i}`} />
              </div>
              <div>
                <Label className="text-[10px]">{t.minStock}</Label>
                <Input type="number" step="0.01" value={r.min_stock}
                       onChange={(e) => upd(i, "min_stock", e.target.value)} />
              </div>
              <div className="flex gap-1 items-end">
                <div className="flex-1">
                  <Label className="text-[10px]">{t.costPerUnit}</Label>
                  <Input type="number" step="0.01" value={r.cost}
                         onChange={(e) => upd(i, "cost", e.target.value)} />
                </div>
                <Button variant="ghost" size="icon" onClick={() => rm(i)}
                        data-testid={`wizard-inv-remove-${i}`}>
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <Button variant="outline" size="sm" onClick={add} data-testid="wizard-inv-add">
        <Plus className="w-4 h-4 mr-1" /> {t.addItem}
      </Button>
    </div>
  );
}

// ============================================================
//  Step 4 — Open Payables
// ============================================================
function StepPayables({ t, rows, setRows }) {
  const add = () => setRows([...rows, { supplier: "", amount: "", due_date: "", recurring: false }]);
  const upd = (i, k, v) => { const c = [...rows]; c[i] = { ...c[i], [k]: v }; setRows(c); };
  const rm = (i) => setRows(rows.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">{t.openPayablesHelp}</p>
      <div className="space-y-2 max-h-[260px] overflow-y-auto pr-1">
        {rows.map((r, i) => (
          <Card key={i} className="border">
            <CardContent className="p-3 grid grid-cols-2 sm:grid-cols-6 gap-2 items-end">
              <div className="col-span-2 sm:col-span-2">
                <Label className="text-[10px]">{t.payableSupplier}</Label>
                <Input value={r.supplier} onChange={(e) => upd(i, "supplier", e.target.value)}
                       data-testid={`wizard-ap-supplier-${i}`} placeholder="MDJ Handels GmbH" />
              </div>
              <div>
                <Label className="text-[10px]">{t.payableAmount}</Label>
                <Input type="number" step="0.01" value={r.amount}
                       onChange={(e) => upd(i, "amount", e.target.value)}
                       data-testid={`wizard-ap-amount-${i}`} />
              </div>
              <div>
                <Label className="text-[10px]">{t.payableDueDate}</Label>
                <Input type="date" value={r.due_date}
                       onChange={(e) => upd(i, "due_date", e.target.value)} />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox checked={!!r.recurring}
                          onCheckedChange={(v) => upd(i, "recurring", v)}
                          id={`ap-rec-${i}`} data-testid={`wizard-ap-recurring-${i}`} />
                <Label htmlFor={`ap-rec-${i}`} className="text-[11px]">{t.payableRecurring}</Label>
              </div>
              <Button variant="ghost" size="icon" onClick={() => rm(i)}>
                <Trash2 className="w-4 h-4 text-destructive" />
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
      <Button variant="outline" size="sm" onClick={add} data-testid="wizard-ap-add">
        <Plus className="w-4 h-4 mr-1" /> {t.addPayable}
      </Button>
    </div>
  );
}

// ============================================================
//  Step 5 — Staff (read-only confirmation)
// ============================================================
function StepStaff({ t, staff, confirmed, setConfirmed }) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">{t.staffHelp}</p>
      <div className="space-y-2">
        {staff.map((s) => (
          <div key={s.id}
               className="flex items-center gap-3 border rounded-lg p-3"
               data-testid={`wizard-staff-${s.id}`}>
            <Checkbox
              checked={!!confirmed[s.id]}
              onCheckedChange={(v) => setConfirmed({ ...confirmed, [s.id]: !!v })}
              id={`staff-${s.id}`}
            />
            <div className="flex-1">
              <div className="font-semibold text-sm">{s.full_name || s.email}</div>
              <div className="text-xs text-muted-foreground">
                {s.email} · {s.role}
                {s.payroll?.role_label ? ` · ${s.payroll.role_label}` : ""}
                {s.payroll?.monthly_net ? ` · ${money(s.payroll.monthly_net)}/${t.dayOfMonth ? "" : ""}mo` : ""}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
//  Step 6 — Recurring Payments
// ============================================================
function StepRecurring({ t, rows, setRows }) {
  const upd = (i, k, v) => { const c = [...rows]; c[i] = { ...c[i], [k]: v }; setRows(c); };
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">{t.recurringHelp}</p>
      <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
        {rows.map((r, i) => (
          <Card key={i} className="border">
            <CardContent className="p-3 grid grid-cols-2 sm:grid-cols-5 gap-2 items-end">
              <div className="col-span-2">
                <Label className="text-[10px]">{t.label}</Label>
                <Input value={r.label} onChange={(e) => upd(i, "label", e.target.value)}
                       data-testid={`wizard-rec-label-${i}`} />
              </div>
              <div>
                <Label className="text-[10px]">{t.amount}</Label>
                <Input type="number" step="0.01" value={r.amount}
                       onChange={(e) => upd(i, "amount", e.target.value)}
                       data-testid={`wizard-rec-amount-${i}`} />
              </div>
              <div>
                <Label className="text-[10px]">{t.dayOfMonth}</Label>
                <Input type="number" min="1" max="31" value={r.day_of_month}
                       onChange={(e) => upd(i, "day_of_month", e.target.value)} />
              </div>
              <div className="text-[11px] text-muted-foreground uppercase tracking-wide">
                {r.category}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ============================================================
//  Step 7 — Confirm
// ============================================================
function StepConfirm({ t, liveStart, bankBalance, cashBalance,
                      inventoryCount, payablesCount, recurringCount, staffCount,
                      guardrails }) {
  return (
    <div className="space-y-4">
      <div className="rounded-lg border-2 border-primary/40 bg-primary/5 p-4">
        <div className="flex items-center gap-2 font-semibold">
          <Rocket className="w-4 h-4 text-primary" /> {t.confirmTitle}
        </div>
        <div className="mt-2 text-sm text-muted-foreground">{t.confirmBody}</div>
        <ul className="mt-3 text-sm space-y-1.5 list-disc pl-5">
          <li><b>{t.li1}</b> <code className="font-mono">{liveStart}</code></li>
          <li>{t.li2}</li>
          <li>{t.li3}</li>
          <li>{t.li4}</li>
        </ul>
      </div>
      {guardrails && guardrails.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3"
             data-testid="wizard-guardrails">
          <div className="text-xs font-semibold uppercase tracking-wider text-amber-900 mb-1.5">
            {t.guardrailsTitle}
          </div>
          <ul className="text-sm text-amber-900 space-y-1 list-disc pl-5">
            {guardrails.map((g, i) => (<li key={i} data-testid={`wizard-guardrail-${i}`}>{g}</li>))}
          </ul>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryTile label={t.bankBalance} value={money(parseFloat(bankBalance || "0") || 0)} testid="confirm-bank" />
        <SummaryTile label={t.cashBalance} value={money(parseFloat(cashBalance || "0") || 0)} testid="confirm-cash" />
        <SummaryTile label={t.openingInventory} value={inventoryCount} testid="confirm-inv" />
        <SummaryTile label={t.openPayables} value={payablesCount} testid="confirm-ap" />
        <SummaryTile label={t.recurring} value={recurringCount} testid="confirm-rec" />
        <SummaryTile label={t.staff} value={`${staffCount} ${t.activeStaff}`} testid="confirm-staff" />
      </div>
    </div>
  );
}

// ============================================================
//  Sprint 18 — Post-launch success view
// ============================================================
function LaunchedView({ t, result, onClose }) {
  const archived = result?.archived || {};
  const totalArchived = Object.values(archived).reduce((s, n) => s + (n || 0), 0);
  return (
    <div className="py-6 space-y-5" data-testid="wizard-launched-view">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
          <CheckCircle2 className="w-6 h-6 text-emerald-600" />
        </div>
        <div>
          <div className="font-display font-semibold text-lg leading-tight">{t.launchedTitle}</div>
          <div className="text-sm text-muted-foreground mt-0.5">
            {t.launchedBody(result?.live_start_date)}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryTile label={t.openingInventory} value={result?.opening_inventory_count ?? 0} testid="launched-inv" />
        <SummaryTile label={t.openPayables} value={result?.open_payables_count ?? 0} testid="launched-ap" />
        <SummaryTile label={t.recurring} value={result?.recurring_payments_count ?? 0} testid="launched-rec" />
        <SummaryTile label="Archive" value={totalArchived} testid="launched-archived" />
      </div>
      <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-xs text-emerald-900">
        {t.canUndo}
      </div>
      <div className="flex justify-end">
        <Button onClick={onClose} data-testid="launched-close">
          {t.goToDashboard}
        </Button>
      </div>
    </div>
  );
}

function SummaryTile({ label, value, testid }) {
  return (
    <div className="border rounded-xl p-3 bg-card">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold mt-1" data-testid={testid}>{value}</div>
    </div>
  );
}
